'use client';

import { useMemo, useState } from 'react';
import { buildQueryString } from '@/utils/buildQueryString';
import { formatDateAndTime, newsBlogFormatDate } from '@/utils/formatDate';
import { RefreshCw } from 'lucide-react';

import apiRequest from '@/lib/api/apiHelper';

import SkeletonUpdate from '../ui/components/LiveNewsUpdateSkelton';

const LiveNewsUpdates = ({ newsId, initialUpdates = [] }: { newsId: string; initialUpdates: any }) => {
  const [data, setData] = useState<any>(initialUpdates);
  const [loading, setLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);

  const fetchLiveUpdates = async () => {
    setLoading(true);
    try {
      const queryParams = {
        populate: 'live_updates',
        filters: {
          news: {
            url: { $eq: newsId },
          },
        },
      };
      const response = await apiRequest(`/news-live-updates?${buildQueryString(queryParams)}`, 'GET');
      setData(response?.data?.data?.results?.[0] ?? []);
    } catch (err) {
      console.error('Failed to fetch live updates:', err);
    } finally {
      setLoading(false);
    }
  };

  const updates = useMemo(() => {
    const list = data?.live_updates ?? [];
    return [...list].sort((a, b) => new Date(b.updated_time).getTime() - new Date(a.updated_time).getTime());
  }, [data]);

  const latestUpdateTime = updates?.[0]?.updated_time ?? data?.updatedAt ?? data?.publishedAt;

  if (!updates?.length) return null;

  return (
    <div className="my-6 w-full">
      <div className="mb-4 flex items-start justify-between gap-3 sm:items-center">
        <div className="flex items-center gap-2 rounded-full bg-red-600 px-4 py-1">
          <span className="size-1 animate-ping rounded-full bg-white"></span>
          <span className="text-base font-thin text-white md:text-base">
            Live <span className="hidden xs:inline-block">Updates</span>
          </span>
        </div>
        <button
          onClick={fetchLiveUpdates}
          className="flex items-center gap-2 rounded-md px-3 py-1 text-xs text-gray-700 hover:bg-gray-100 md:border"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Updated: {newsBlogFormatDate(latestUpdateTime)}
        </button>
      </div>

      <div className="relative pl-6">
        <div className="absolute left-10 top-0 hidden h-full w-0.5 bg-gray-300 sm:block md:left-2" />
        <div className="space-y-5">
          {loading
            ? Array.from({ length: 5 }).map((_, idx) => <SkeletonUpdate key={idx} />)
            : updates?.slice(0, visibleCount).map((update: any) => {
                const { time, date } = formatDateAndTime(update?.updated_time);
                return (
                  <div key={update?.id} className="relative flex items-start gap-4">
                    <div className="absolute left-[-21px] top-2 flex size-3 items-center justify-center">
                      <span className="size-3 rounded-full bg-red-500 ring-4 ring-white" />
                    </div>
                    <div className="pl-2 pt-1 md:pl-6">
                      <div className="space-x-2 text-sm font-semibold">
                        <span className="uppercase text-red-500">{time}</span>
                        <span className="text-gray-400">-</span>
                        <span className="text-gray-500">{date}</span>
                      </div>

                      <h3 className="text-base font-semibold text-gray-800 md:text-lg">{update?.heading}</h3>
                      <p className="whitespace-pre-line pt-2 text-sm text-gray-700 md:text-base">
                        {update?.description}
                      </p>
                    </div>
                  </div>
                );
              })}
        </div>
      </div>

      {visibleCount < updates.length && (
        <div className="mt-6 flex items-center justify-center">
          <button
            onClick={() => setVisibleCount((prev) => prev + 5)}
            className="rounded-md bg-primary px-4 py-2 text-sm text-white"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default LiveNewsUpdates;
